import React, { useEffect, useState } from "react";
import GuestForm from "../components/forms/GuestForm";
import ThankYouMessage from "../components/ui-custom/ThankYouMessage";
import { getPublicConfig } from "@/api/publicConfig";
import { captureAttribution } from "@/utils/attribution";
import { Mail, Phone, Clock, ShieldCheck, Zap, CheckCircle2, ExternalLink, ArrowRight } from "lucide-react";

interface TrackingIDs {
  fbPixelId: string | null;
  gaId: string | null;
}

// Extended window interface with proper Facebook Pixel typing
declare global {
  interface Window {
    fbq: any; // Using any for fbq since it has many properties and methods
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

const Index = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [trackingIds, setTrackingIds] = useState<TrackingIDs>({
    fbPixelId: null,
    gaId: null,
  });

  // Remember where this visitor came from (utm_*, fbclid, gclid, variant) before
  // they navigate anywhere — this is what lets us compare website leads against
  // Facebook lead-form leads later.
  useEffect(() => {
    captureAttribution();
  }, []);

  useEffect(() => {
    const fetchTrackingIDs = async () => {
      try {
        const data = await getPublicConfig();
        setTrackingIds({
          fbPixelId: data.fb_pixel_id,
          gaId: data.ga_id,
        });
      } catch (error) {
        console.error('Unexpected error fetching tracking IDs:', error);
      }
    };
    
    fetchTrackingIDs();
  }, []);
  
  useEffect(() => {
    // Initialize Facebook Pixel
    if (trackingIds.fbPixelId) {
      const initFacebookPixel = () => {
        if (!window.fbq) {
          const fbqStub: any = (...args: any[]) => {
            if (fbqStub.callMethod) {
              fbqStub.callMethod(...args);
            } else {
              fbqStub.queue.push(args);
            }
          };

          fbqStub.queue = [];
          fbqStub.callMethod = null;
          fbqStub.loaded = true;
          fbqStub.version = '2.0';
          window.fbq = fbqStub;
        }

        const existingScript = document.querySelector('script[src="https://connect.facebook.net/en_US/fbevents.js"]');
        if (!existingScript) {
          const script = document.createElement('script');
          script.async = true;
          script.src = 'https://connect.facebook.net/en_US/fbevents.js';
          document.head.appendChild(script);
        }

        window.fbq('init', trackingIds.fbPixelId);
        window.fbq('track', 'PageView');
      };

      if (typeof window !== 'undefined') {
        initFacebookPixel();
      }
    }
  }, [trackingIds.fbPixelId]);
  
  useEffect(() => {
    // Initialize Google Analytics
    if (trackingIds.gaId) {
      const initGoogleAnalytics = () => {
        // Load the GA script
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingIds.gaId}`;
        const existingGaScript = document.querySelector(`script[src="https://www.googletagmanager.com/gtag/js?id=${trackingIds.gaId}"]`);
        if (!existingGaScript) {
          document.head.appendChild(script);
        }

        window.dataLayer = window.dataLayer || [];
        function gtag(...args: any[]) {
          window.dataLayer.push(args);
        }
        window.gtag = gtag;
        gtag('js', new Date());
        gtag('config', trackingIds.gaId);
      };

      // If gtag is not already defined, initialize it
      if (typeof window !== 'undefined' && typeof window.gtag === 'undefined') {
        initGoogleAnalytics();
      }
    }
  }, [trackingIds.gaId]);

  const handleSubmitSuccess = () => {
    // The Facebook Lead event is fired inside GuestForm with an eventID that is
    // shared with the Conversions API copy, so Meta can deduplicate the pair.
    // Firing it again here would report the same conversion twice.

    if (trackingIds.gaId && window.gtag) {
      window.gtag('event', 'generate_lead', {
        'event_category': 'engagement',
        'event_label': 'form_submission'
      });
    }
    
    setIsSubmitted(true);
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-20 border-b border-slate-200/80 bg-white/95 backdrop-blur">
        <div className="container mx-auto px-4 py-3.5">
          <div className="flex items-center justify-between gap-3">
            <a
              href="https://diathermiki.gr"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 group min-w-0"
            >
              <img
                src="https://diathermiki.gr/assets/diathermiki-square-D4zI0y5P.jpg"
                alt="Diathermiki Logo"
                className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl object-cover ring-1 ring-slate-200"
              />
              <div className="text-left min-w-0">
                <h1 className="text-base sm:text-xl font-bold text-slate-900 truncate">Διαθερμική Α.Ε.</h1>
                <p className="hidden sm:block text-sm text-slate-500">Ενεργειακές λύσεις με κορυφαίο δίκτυο συνεργατών</p>
              </div>
            </a>

            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              <a href="tel:+302310535975">
                <button className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                  <Phone className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Κλήση</span>
                </button>
              </a>
              <a href="https://diathermiki.gr" target="_blank" rel="noopener noreferrer">
                <button className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-white hover:bg-slate-800 transition-colors">
                  <span className="hidden sm:inline">Επίσημο Site</span>
                  <span className="sm:hidden">Site</span>
                  <ExternalLink className="h-3.5 w-3.5" />
                </button>
              </a>
            </div>
          </div>
        </div>
      </header>
      
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          {isSubmitted ? (
            <ThankYouMessage />
          ) : (
            <div className="bg-white shadow-lg rounded-2xl overflow-hidden border border-slate-100">
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-5 py-5 sm:px-6 sm:py-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-white">Φόρμα Ενδιαφέροντος</h1>
                <p className="text-blue-100 mt-1 text-sm sm:text-base">
                  Συμπληρώστε τα στοιχεία σας και λάβετε άμεση επικοινωνία από εξειδικευμένο συνεργάτη.
                </p>
                <div className="mt-3 sm:mt-4 grid grid-cols-3 gap-1.5 sm:gap-2.5">
                  <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 rounded-lg bg-white/10 px-2 sm:px-3 py-2 text-blue-50 text-[11px] sm:text-sm text-center sm:text-left">
                    <Zap className="h-4 w-4 shrink-0" />
                    Άμεση απόκριση
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 rounded-lg bg-white/10 px-2 sm:px-3 py-2 text-blue-50 text-[11px] sm:text-sm text-center sm:text-left">
                    <ShieldCheck className="h-4 w-4 shrink-0" />
                    Ασφαλή δεδομένα
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 rounded-lg bg-white/10 px-2 sm:px-3 py-2 text-blue-50 text-[11px] sm:text-sm text-center sm:text-left">
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    Δωρεάν εκτίμηση
                  </div>
                </div>
              </div>
              
              <div className="p-4 sm:p-6">
                <GuestForm onSubmitSuccess={handleSubmitSuccess} />
              </div>
            </div>
          )}
        </div>
      </main>
      
      <footer className="mt-12 border-t border-slate-200 bg-white">
        <div className="container mx-auto px-4">
          <div className="py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img
                  src="https://diathermiki.gr/assets/diathermiki-square-D4zI0y5P.jpg"
                  alt="Διαθερμική Logo"
                  className="h-11 w-11 rounded-lg object-cover ring-1 ring-slate-200"
                />
                <h3 className="text-lg font-bold text-slate-900">Διαθερμική Α.Ε.</h3>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed">
                Το μεγαλύτερο δίκτυο ενεργειακών, τεχνικών και καταστημάτων στην Ελλάδα.
              </p>
              <a
                href="https://diathermiki.gr"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900"
              >
                Επίσκεψη στο diathermiki.gr
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
            
            <div className="space-y-3">
              <h4 className="text-base font-semibold text-slate-900">Επικοινωνία</h4>
              <div className="space-y-2.5 text-slate-600 text-sm">
                <p className="flex items-center">
                  <Phone className="h-4 w-4 mr-2 text-slate-400" />
                  <a href="tel:+302310535975" className="hover:text-slate-900">+30 2310 535975</a>
                </p>
                <p className="flex items-center">
                  <Phone className="h-4 w-4 mr-2 text-slate-400" />
                  <a href="tel:+302310536696" className="hover:text-slate-900">+30 2310 536696</a>
                </p>
                <p className="flex items-center">
                  <Mail className="h-4 w-4 mr-2 text-slate-400" />
                  <a href="mailto:info@diathermiki.gr" className="hover:text-slate-900">info@diathermiki.gr</a>
                </p>
              </div>
            </div>
            
            <div className="space-y-3">
              <h4 className="text-base font-semibold text-slate-900">Ωράριο & Δεδομένα</h4>
              <div className="space-y-2 text-slate-600 text-sm">
                <p className="flex items-start">
                  <Clock className="h-4 w-4 mr-2 text-slate-400 mt-0.5 flex-shrink-0" />
                  <span>Για άμεση ενημέρωση ωραρίου, επικοινωνήστε με το κατάστημα της περιοχής σας.</span>
                </p>
                <p className="flex items-start">
                  <ShieldCheck className="h-4 w-4 mr-2 text-slate-400 mt-0.5 flex-shrink-0" />
                  <span>Τα στοιχεία σας χρησιμοποιούνται αποκλειστικά για εξυπηρέτηση του αιτήματος.</span>
                </p>
              </div>
              <div className="pt-1">
                <a href="https://diathermiki.gr" target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-slate-700 hover:text-slate-900 inline-flex items-center gap-1">
                  Πολιτική προστασίας προσωπικών δεδομένων
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-slate-200 py-5 text-center text-slate-500 text-sm">
            <p>&copy; {new Date().getFullYear()} Διαθερμική Α.Ε. Με επιφύλαξη παντός δικαιώματος.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
